import type { HTMLProps, ReactNode } from 'react';
import { forwardRef } from 'react';

import { Color } from '@tmh-website/shared/colors';
import StyledTypo from '@tmh-website/styles/typo';

import * as Styled from './ButtonText.styles';

export interface ButtonTextProps
  extends Omit<HTMLProps<HTMLButtonElement | HTMLAnchorElement>, 'type' | 'ref' | 'as' | 'forwardedAs'> {
  children?: ReactNode;
  /**
   * Optional custom Icon on the left
   */
  iconLeft?: any;
  /**
   * Optional custom Icon on the right
   */
  iconRight?: any;
  /**
   * Disable the transition of the icon moving outwards
   */
  iconMovesOnHover?: boolean;
  /**
   * Optional custom gap between text and icon
   */
  gap?: string;
  hasPadding?: boolean;
  textColor?: Color;
  textHoverColor?: Color;
  iconColor?: Color;
  iconHoverColor?: Color;
  transparentHover?: boolean;
  /**
   * Force a hover state e.g. in case that the parent controls the hover
   */
  forceHover?: boolean;
  type?: 'button' | 'reset' | 'submit' | undefined;
  fontWeight?: 'regular' | 'bold';
}

/**
 * A text only button that only has a text hover effect.
 * Can optionally have an icon (left or right) that is optionally animated.
 */
const ButtonText = forwardRef<HTMLButtonElement & Partial<HTMLAnchorElement>, ButtonTextProps>(
  (
    {
      children,
      gap = '8px',
      hasPadding = false,
      iconLeft,
      iconRight,
      iconMovesOnHover = true,
      textColor,
      textHoverColor = Color.Blue_500_Ultramarine,
      iconColor,
      iconHoverColor = Color.Blue_500_Ultramarine,
      forceHover = false,
      transparentHover = false,
      fontWeight = 'bold',
      type,
      ...props
    },
    ref
  ) => {
    return (
      <Styled.Container
        as={props.href ? 'a' : 'button'}
        type={type}
        {...props}
        ref={ref}
        $gap={gap}
        $hasPadding={hasPadding}
        $forceHover={!!forceHover}
        $textColor={textColor}
        $textHoverColor={textHoverColor}
        $iconHoverColor={iconHoverColor}
        $iconMovesOnHover={iconMovesOnHover}
        $transparentHover={transparentHover}>
        {iconLeft && <Styled.IconContainerLeft>{iconLeft}</Styled.IconContainerLeft>}
        <StyledTypo.Typography $style={fontWeight === 'bold' ? 'uiTextBold' : 'uiText'}>
          {children}
        </StyledTypo.Typography>
        {iconRight && <Styled.IconContainerRight>{iconRight}</Styled.IconContainerRight>}
      </Styled.Container>
    );
  }
);

ButtonText.displayName = 'ButtonText';

export default ButtonText;
